import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import ContentConstants from '../contentConstants';
import { ICustomDimensionEditModel, ISurveyDimension, ISurveyQuestionEditModel, IValidationErrorCollection } from '../models';
import * as dimensionAPI from '../services/DimensionAPI';
import { ActionTypes as CommonActionTypes } from './ActionTypes';
import { mapDimensionEditModelToSurveyDimension } from '../utilities/DimensionHelper';

export interface ISurveyDimensionsState {
	isLoadingSurveyDimensions: boolean;
	surveyDimensions: ISurveyDimension[];
	isSavingSurveyDimension: boolean;
	validationErrors?: IValidationErrorCollection;
}

export const initialState: ISurveyDimensionsState = {
	isLoadingSurveyDimensions: false,
	surveyDimensions: [],
	isSavingSurveyDimension: false,
};

const handleError = (error: unknown, rejectWithValue: any) => {
	const axiosError = error as AxiosError;
	if (axiosError && axiosError.isAxiosError) {
		return rejectWithValue(axiosError.response?.data.validationErrors.validations);
	} else {
		return rejectWithValue([]);
	}
};

export const loadSurveyDimensions = createAsyncThunk(
	'surveyDimensions/loadSurveyDimensions',
	async (surveyId: number, { dispatch, rejectWithValue }) => {
		try {
			const surveyDimensions = await dimensionAPI.getSurveyDimensions<ISurveyDimension>(surveyId, true);
			return { surveyDimensions };
		} catch (error) {
			dispatch({ type: CommonActionTypes.TOAST_WARNING_NOTIFICATION, payload: ContentConstants.TOAST_DIMENSION_LOAD_FAILED });
			rejectWithValue(error);
		}
	},
);

export const createSurveyDimension = createAsyncThunk(
	'surveyDimensions/createSurveyDimension',
	async (
		{
			surveyDimension,
			onSuccess,
		}: { surveyDimension: ICustomDimensionEditModel; onSuccess?: (createdDimension: ISurveyDimension) => void },
		{ dispatch, rejectWithValue },
	) => {
		try {
			dispatch({ type: CommonActionTypes.TOAST_PROCESSING_GENERIC_NOTIFICATION });

			const createdDimension = await dimensionAPI.addSurveyDimension(surveyDimension);

			dispatch({ type: CommonActionTypes.TOAST_INFO_NOTIFICATION, payload: ContentConstants.TOAST_DIMENSION_DIMENSION_ADDED });

			const mappedDimension = mapDimensionEditModelToSurveyDimension(createdDimension);
			onSuccess?.(mappedDimension);

			return mappedDimension;
		} catch (error) {
			dispatch({ type: CommonActionTypes.TOAST_WARNING_NOTIFICATION, payload: ContentConstants.TOAST_DIMENSION_ADD_FAILED });

			return handleError(error, rejectWithValue);
		}
	},
);

export const editSurveyDimension = createAsyncThunk(
	'surveyDimensions/editSurveyDimension',
	async (
		{
			surveyDimension,
			onSuccess,
		}: { surveyDimension: ICustomDimensionEditModel; onSuccess?: (updatedDimension: ISurveyDimension) => void },
		{ dispatch, rejectWithValue },
	) => {
		try {
			dispatch({ type: CommonActionTypes.TOAST_PROCESSING_GENERIC_NOTIFICATION });

			const updatedDimension = await dimensionAPI.editSurveyDimension(surveyDimension);

			dispatch({ type: CommonActionTypes.TOAST_INFO_NOTIFICATION, payload: ContentConstants.TOAST_DIMENSION_DIMENSION_SAVED });

			const mappedDimension = mapDimensionEditModelToSurveyDimension(updatedDimension);
			onSuccess?.(mappedDimension);

			return mappedDimension;
		} catch (error) {
			dispatch({ type: CommonActionTypes.TOAST_WARNING_NOTIFICATION, payload: ContentConstants.TOAST_DIMENSION_EDIT_FAILED });

			return handleError(error, rejectWithValue);
		}
	},
);

export const addSurveyQuestionsToDimension = createAsyncThunk(
	'surveyDimensions/addSurveyQuestionsToDimension',
	async (
		{
			surveyId,
			surveyDimensionId,
			surveyQuestions,
		}: {
			surveyId: number;
			surveyDimensionId: number;
			surveyQuestions: ISurveyQuestionEditModel[];
		},
		{ dispatch, rejectWithValue },
	) => {
		try {
			dispatch({ type: CommonActionTypes.TOAST_PROCESSING_GENERIC_NOTIFICATION });

			await dimensionAPI.addQuestionsToSurveyDimension(
				surveyId,
				surveyDimensionId,
				surveyQuestions.map((q) => q.surveyQuestionId!),
			);

			dispatch({ type: CommonActionTypes.TOAST_INFO_NOTIFICATION, payload: ContentConstants.TOAST_DIMENSION_QUESTION_MOVE_SUCCESS });

			return { surveyDimensionId, surveyQuestions };
		} catch (error) {
			dispatch({
				type: CommonActionTypes.TOAST_WARNING_NOTIFICATION,
				payload: ContentConstants.TOAST_DIMENSION_QUESTION_MOVE_FAILED,
			});

			return handleError(error, rejectWithValue);
		}
	},
);

export const moveSurveyQuestionsToNewDimension = createAsyncThunk(
	'surveyDimensions/moveSurveyQuestionsToNewDimension',
	async (
		{
			surveyId,
			sourceSurveyDimensionId,
			targetSurveyDimensionId,
			questionsToMove,
		}: {
			surveyId: number;
			sourceSurveyDimensionId: number;
			targetSurveyDimensionId: number;
			questionsToMove: ISurveyQuestionEditModel[];
		},
		{ dispatch, rejectWithValue },
	) => {
		try {
			dispatch({ type: CommonActionTypes.TOAST_PROCESSING_GENERIC_NOTIFICATION });

			await dimensionAPI.moveQuestionsToNewSurveyDimension(surveyId, sourceSurveyDimensionId, {
				targetSurveyDimensionId,
				surveyQuestionIds: questionsToMove.map((q) => q.surveyQuestionId!),
			});

			dispatch({ type: CommonActionTypes.TOAST_INFO_NOTIFICATION, payload: ContentConstants.TOAST_DIMENSION_QUESTION_MOVE_SUCCESS });

			return {
				sourceSurveyDimensionId,
				targetSurveyDimensionId,
				questionsToMove,
			};
		} catch (error) {
			dispatch({
				type: CommonActionTypes.TOAST_WARNING_NOTIFICATION,
				payload: ContentConstants.TOAST_DIMENSION_QUESTION_MOVE_FAILED,
			});

			return handleError(error, rejectWithValue);
		}
	},
);

export const removeSurveyQuestionsFromDimension = createAsyncThunk(
	'surveyDimensions/removeSurveyQuestionsFromDimension',
	async (
		{
			surveyId,
			surveyDimensionId,
			questionsToRemove,
		}: {
			surveyId: number;
			surveyDimensionId: number;
			questionsToRemove: ISurveyQuestionEditModel[];
		},
		{ dispatch, rejectWithValue },
	) => {
		try {
			dispatch({ type: CommonActionTypes.TOAST_PROCESSING_GENERIC_NOTIFICATION });

			await dimensionAPI.removeQuestionsFromDimension(
				surveyId,
				surveyDimensionId,
				questionsToRemove.map((q) => q.surveyQuestionId!),
			);

			dispatch({ type: CommonActionTypes.TOAST_INFO_NOTIFICATION, payload: ContentConstants.TOAST_DIMENSION_REMOVE_QUESTION_SUCCESS });

			return { surveyDimensionId, questionsToRemove };
		} catch (error) {
			dispatch({
				type: CommonActionTypes.TOAST_WARNING_NOTIFICATION,
				payload: ContentConstants.TOAST_DIMENSION_REMOVE_QUESTION_FAILED,
			});

			return handleError(error, rejectWithValue);
		}
	},
);

export const deleteDimension = createAsyncThunk(
	'surveyDimensions/deleteDimension',
	async ({ surveyId, dimensionId }: { surveyId: number; dimensionId: number }, { dispatch }) => {
		try {
			await dimensionAPI.removeSurveyDimension(surveyId, dimensionId);

			dispatch({ type: CommonActionTypes.TOAST_INFO_NOTIFICATION, payload: ContentConstants.TOAST_REMOVE_DIMENSION_SUCCESS });

			return dimensionId;
		} catch {
			dispatch({
				type: CommonActionTypes.TOAST_WARNING_NOTIFICATION,
				payload: ContentConstants.TOAST_REMOVE_DIMENSION_FAILED,
			});
		}
	},
);

const surveyDimensionSlice = createSlice({
	name: 'surveyDimensions',
	initialState: initialState,
	reducers: {},
	extraReducers: (builder) => {
		builder
			.addCase(loadSurveyDimensions.pending, (state) => {
				state.isLoadingSurveyDimensions = true;
			})
			.addCase(loadSurveyDimensions.fulfilled, (state, action) => {
				state.surveyDimensions = action.payload?.surveyDimensions ?? [];
				state.isLoadingSurveyDimensions = false;
				state.validationErrors = undefined;
			})
			.addCase(loadSurveyDimensions.rejected, (state) => {
				state.isLoadingSurveyDimensions = false;
			})
			.addCase(createSurveyDimension.pending, (state) => {
				state.isSavingSurveyDimension = true;
			})
			.addCase(createSurveyDimension.fulfilled, (state, action) => {
				state.isSavingSurveyDimension = false;
				state.validationErrors = undefined;
				state.surveyDimensions.push(action.payload);
			})
			.addCase(createSurveyDimension.rejected, (state, action) => {
				state.isSavingSurveyDimension = false;
				state.validationErrors = action.payload as IValidationErrorCollection;
			})
			.addCase(editSurveyDimension.pending, (state) => {
				state.isSavingSurveyDimension = true;
			})
			.addCase(editSurveyDimension.fulfilled, (state, action) => {
				state.isSavingSurveyDimension = false;
				state.validationErrors = undefined;
				state.surveyDimensions = state.surveyDimensions.map((dimension: ISurveyDimension) => {
					if (dimension.dimensionId === action.payload.dimensionId) {
						return action.payload;
					} else {
						return dimension;
					}
				});
			})
			.addCase(editSurveyDimension.rejected, (state, action) => {
				state.isSavingSurveyDimension = false;
				state.validationErrors = action.payload as IValidationErrorCollection;
			})
			.addCase(addSurveyQuestionsToDimension.pending, (state) => {
				state.isSavingSurveyDimension = true;
			})
			.addCase(addSurveyQuestionsToDimension.fulfilled, (state, action) => {
				const { surveyDimensionId, surveyQuestions } = action.payload;
				state.surveyDimensions = state.surveyDimensions.map((d) => {
					if (d.dimensionId === surveyDimensionId) {
						return {
							...d,
							surveyQuestions: d.surveyQuestions ? [...d.surveyQuestions, ...surveyQuestions] : surveyQuestions,
						};
					} else {
						return d;
					}
				});
				state.isSavingSurveyDimension = false;
			})
			.addCase(addSurveyQuestionsToDimension.rejected, (state, action) => {
				state.isSavingSurveyDimension = false;
				state.validationErrors = action.payload as IValidationErrorCollection;
			})
			.addCase(moveSurveyQuestionsToNewDimension.pending, (state) => {
				state.isSavingSurveyDimension = true;
			})
			.addCase(moveSurveyQuestionsToNewDimension.fulfilled, (state, action) => {
				const { sourceSurveyDimensionId, targetSurveyDimensionId, questionsToMove } = action.payload;
				state.surveyDimensions = state.surveyDimensions.map((d) => {
					if (d.dimensionId === sourceSurveyDimensionId) {
						return {
							...d,
							surveyQuestions: d.surveyQuestions?.filter(
								(q) => questionsToMove.findIndex((qtm: ISurveyQuestionEditModel) => qtm.surveyQuestionId === q.surveyQuestionId) === -1,
							),
						};
					} else if (d.dimensionId === targetSurveyDimensionId) {
						return {
							...d,
							surveyQuestions: d.surveyQuestions ? [...d.surveyQuestions, ...questionsToMove] : questionsToMove,
						};
					} else {
						return d;
					}
				});
				state.isSavingSurveyDimension = false;
			})
			.addCase(moveSurveyQuestionsToNewDimension.rejected, (state, action) => {
				state.isSavingSurveyDimension = false;
				state.validationErrors = action.payload as IValidationErrorCollection;
			})
			.addCase(removeSurveyQuestionsFromDimension.pending, (state) => {
				state.isSavingSurveyDimension = true;
			})
			.addCase(removeSurveyQuestionsFromDimension.fulfilled, (state, action) => {
				const { surveyDimensionId, questionsToRemove } = action.payload;
				state.surveyDimensions = state.surveyDimensions.map((d) => {
					if (d.dimensionId === surveyDimensionId) {
						return {
							...d,
							surveyQuestions: d.surveyQuestions?.filter(
								(q) => questionsToRemove.findIndex((qtr: ISurveyQuestionEditModel) => qtr.surveyQuestionId === q.surveyQuestionId) === -1,
							),
						};
					} else {
						return d;
					}
				});
				state.isSavingSurveyDimension = false;
			})
			.addCase(removeSurveyQuestionsFromDimension.rejected, (state) => {
				state.isLoadingSurveyDimensions = false;
			})
			.addCase(deleteDimension.pending, (state) => {
				state.isSavingSurveyDimension = true;
			})
			.addCase(deleteDimension.fulfilled, (state, action) => {
				state.surveyDimensions = state.surveyDimensions.filter((d) => d.dimensionId !== action.payload);
				state.isSavingSurveyDimension = false;
			});
	},
});

export default surveyDimensionSlice.reducer;
